import "./ListItem.scss";

import { CSSProperties, FC, HTMLProps, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";

import Divider from "components/Divider/Divider";
import Icon, { IconColor } from "components/Icon/Icon";
import Text, { TextType } from "components/Text/Text";
import ToggleInput, { IToggleInputEvent } from "components/ToggleInput/ToggleInput";
import { ClassName } from "config/types";

import classNames from "classnames";
import { defaultTo, isNil, isString, kebabCase } from "lodash-es";

export const elementTypes = {
  li: "li",
  div: "div",
  a: "a",
};

export type ElementType = keyof typeof elementTypes;

type IProps = HTMLProps<HTMLLIElement> & {
  className?: ClassName;
  style?: CSSProperties;
  header?: string;
  text?: ReactNode;
  textTag?: TextType;
  disabled?: boolean;
  isHeader?: boolean;
  isLabel?: boolean;
  textBold?: boolean;
  secondaryText?: string;
  rightText?: string;
  rightTextTag?: TextType;
  rightSecondaryText?: string;
  icon?: {
    left?: string;
    right?: string;
    colorRight?: IconColor;
  };
  toggle?: {
    checked: boolean;
    onChange: (event: IToggleInputEvent) => void;
  };
  dataTestId?: string;
  imgSrc?: string;
  tag?: ElementType;
  linkTo?: string;
  active?: boolean;
  heightAuto?: boolean;
  noBorderBottom?: boolean;
  elementRef?: React.Ref<HTMLInputElement> | null;
  onClick?: (event: MouseEvent) => void;
  onRightIconClick?: (event: MouseEvent) => void;
};

const getTextContentTag = (
  tag: TextType = "l3",
  isLabel?: boolean,
  isHeader?: boolean
): TextType => {
  if (isLabel) {
    return "p5";
  }
  if (isHeader) {
    return "c5";
  }
  return tag;
};

const LinkContent: FC<{
  linkTo: string;
  isHeader?: boolean;
  rightText?: string;
  iconRight?: string;
  dataTestIdBase: string;
}> = ({ linkTo, isHeader, rightText, iconRight, dataTestIdBase }) => (
  <Link className="link list-link" to={linkTo || ""}>
    <Text tag={isHeader ? "c5" : "l3"} text={rightText} className="list-item__text" />
    <Icon
      src={iconRight}
      className="list-item__right-icon list-item__right-icon--link"
      dataTestId={`list-item__right-icon--${dataTestIdBase}`}
    />
  </Link>
);

const TextContent: FC<{
  text?: ReactNode;
  textTag?: TextType;
  isLabel?: boolean;
  isHeader?: boolean;
}> = ({ text, textTag = "l3", isLabel, isHeader }) => (
  <Text
    tag={getTextContentTag(textTag, isLabel, isHeader)}
    text={text}
    className="list-item__text"
  />
);

const SecondaryTextContent: FC<{ secondaryText?: string; isLabel?: boolean }> = ({
  secondaryText,
  isLabel,
}) => {
  return secondaryText ? (
    <Text tag={isLabel ? "l3" : "p5"} text={secondaryText} className="list-item__text--secondary" />
  ) : null;
};

const ListItem: FC<IProps> = props => {
  const {
    children,
    className,
    elementRef,
    dataTestId: dataTestIdProp,
    style,
    text,
    disabled,
    isHeader,
    isLabel,
    textBold,
    textTag,
    secondaryText,
    rightText,
    rightTextTag,
    rightSecondaryText,
    icon,
    toggle,
    imgSrc,
    tag,
    linkTo,
    active,
    heightAuto,
    noBorderBottom,
    onClick,
    onRightIconClick,
    ...nativeProps
  } = props;

  const defaultTag = defaultTo(tag, "li");
  const Tag = elementTypes[defaultTag] as "li" | "div" | "a";
  const textLabel = isString(text) ? kebabCase(text) : "";
  const dataTestIdBase = dataTestIdProp || textLabel;

  const classes = classNames("list-item", className, {
    "list-item--disabled": disabled,
    "list-item--active": active,
    "list-item--height-auto": heightAuto,
    "list-item--border-bottom": !isHeader && !noBorderBottom,
  });

  const leftContentClasses = classNames("list-item__left-container", {
    "list-item__text--header": isHeader || textBold,
    "single-line-list": !rightText && !rightSecondaryText,
  });

  const leftTextClasses = classNames("list-item__left-container_text", {
    "label-list-item": isLabel,
  });

  const rightTextClasses = classNames("list-item__right-container_text", {
    "label-list-item": isLabel,
  });

  return (
    <Tag
      {...(nativeProps as React.HTMLAttributes<HTMLElement>)}
      ref={elementRef as React.Ref<any>}
      className={classes}
      style={style}
      onClick={onClick as React.MouseEventHandler<HTMLElement>}
      data-testid={`list-item__${dataTestIdBase}`}>
      {isHeader && <Divider />}
      <div className="list-item__content flex--center-space-between">
        <div className={leftContentClasses}>
          {imgSrc && <img alt="ALT ART" src={imgSrc} className="list-item__image" />}
          {icon?.left && <Icon src={icon.left} className="list-item__left-icon" />}
          <div className={leftTextClasses}>
            <TextContent text={text} textTag={textTag} isLabel={isLabel} isHeader={isHeader} />
            <SecondaryTextContent secondaryText={secondaryText} isLabel={isLabel} />
          </div>
        </div>

        <div className="list-item__right-container">
          <div className={rightTextClasses}>
            {linkTo ? (
              <LinkContent
                linkTo={linkTo}
                isHeader={isHeader}
                rightText={rightText}
                iconRight={icon?.right}
                dataTestIdBase={dataTestIdBase}
              />
            ) : null}
            {!linkTo && rightText ? (
              <TextContent
                text={rightText}
                textTag={rightTextTag}
                isLabel={isLabel}
                isHeader={isHeader}
              />
            ) : null}

            <SecondaryTextContent secondaryText={rightSecondaryText} isLabel={isLabel} />
          </div>
          {icon?.right && !linkTo && (
            <Icon
              onClick={onRightIconClick}
              src={icon.right}
              className="list-item__right-icon"
              dataTestId={`list-item__right-icon--${dataTestIdBase}--${icon.right}`}
              color={icon?.colorRight || "primary-dark"}
            />
          )}
          {!isNil(toggle) && <ToggleInput checked={toggle.checked} onChange={toggle.onChange} />}
        </div>
      </div>
      {children}
    </Tag>
  );
};

export default ListItem;
